import {useEffect} from "react";
import TreeListItem, { type TreeListItemData } from "./TreeListItem";
import UpgradePanel from "./UpgradePanel";
import "./TreeList.css";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {getJobs, getMe} from "../../api/Client";
import {useParams} from "react-router-dom";

interface TreeListProps {
  onSelectTree?: (id: string) => void;
  onRenameTree?: (id: string) => void;
  onDeleteTree?: (id: string) => void;
}

/** Returns a new array, oldest first — never mutates the input. */
function byCreationOrder(trees: TreeListItemData[]): TreeListItemData[] {
  return [...trees].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}

export default function TreeList({
  onSelectTree,
  onRenameTree,
  onDeleteTree,
}: TreeListProps) {
  // const ordered = byCreationOrder(trees);

  const { jobId } = useParams<{ jobId: string }>();
  const queryClient = useQueryClient();

  const {data, isLoading} = useQuery({
    queryKey: ["jobs"],
    queryFn: getJobs,
    refetchInterval: (query) => {
      const jobs = query.state.data;

      const hasIncompleteJobs = jobs?.some(
          (job: any) => job.status !== "COMPLETE"
      );

      return hasIncompleteJobs ? 2000 : false;
    },
  });

  // Learn's main panel polls this same job's status independently (its own
  // ["job", jobId] query, its own 2s timer) — without this, whichever of the
  // two happens to poll first shows a status the other hasn't caught up to
  // yet. Depends on the primitive status value, not `data` itself, so this
  // only fires on an actual transition and can't loop against Learn's own
  // matching invalidation of ["jobs"].
  const activeJobStatus = data?.find((job: any) => job.id === jobId)?.status;
  useEffect(() => {
    if (jobId && activeJobStatus) {
      queryClient.invalidateQueries({ queryKey: ["job", jobId] });
    }
  }, [jobId, activeJobStatus, queryClient]);

  // Same cache entry ProfileButton/Learn read — no extra request.
  const {data: me} = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
  });

  return (
      <nav className="tree-list" aria-label="Your trees">
        <div className="tree-list__scroll">
          <span className="eyebrow tree-list__heading">Your trees</span>

          {isLoading || data.length === 0 ? (
              <p className="tree-list__empty">
                No trees yet — generate one from a syllabus to get started.
              </p>
          ) : (
              <div className="tree-list__items">
                {data.map((job: any) => (
                        <TreeListItem
                            key={job.id}
                            tree={{
                              id: job.id,
                              title: job.subject?.title || job.prompt,
                              // Chapters completed out of total chapters —
                              // _count was previously being passed through
                              // whole (an object, not a number) and solved
                              // read `solvedNodes`, a field that's never
                              // actually incremented anywhere in the backend.
                              solved: job.subject?.chaptersCompleted || 0,
                              total: job.subject?._count?.chapters || 0,
                              createdAt: job.createdAt
                            }}
                            status={job.status}
                            questionsReady={(job._count?.questions ?? 0) > 0}
                            active={job.id === jobId}
                            onSelect={onSelectTree}
                            onRename={onRenameTree}
                            onDelete={onDeleteTree}
                        />
                    )
                )}
              </div>
          )}
        </div>

        {!me?.subscribed && <UpgradePanel />}
      </nav>
  );
}